import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, MessageCircle } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { WHATSAPP_URL } from "@/components/landing/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANSWERS = {
  bene: { label: "Sto bene", title: "Che bella notizia.", text: "Le tue gengive stanno guarendo come dovrebbero. Continua con cibi morbidi ancora per qualche giorno, spazzolino delicato e tanta acqua. Ci vediamo al controllo." },
  fastidio: { label: "Un po' di fastidio", title: "È normale, non preoccuparti.", text: "Nei primi giorni un po' di sensibilità è frequente e si attenua da sola. Preferisci cibi tiepidi e morbidi, evita di spazzolare con forza la zona trattata e segui le indicazioni ricevute in studio. Il Dott. Mauro è stato avvisato: se il fastidio aumenta, scrivici." },
  aiuto: { label: "Ho bisogno di aiuto", title: "Siamo qui per te.", text: "Il Dott. Mauro è stato avvisato e ti ricontatterà al più presto. Se preferisci, scrivici subito su WhatsApp: rispondiamo con calma a ogni domanda." },
};

const Choice = ({ k, active, onPick }) => (
  <button onClick={() => onPick(k)} data-testid={`checkin-answer-${k}`}
    className={`rounded-full px-5 py-3 text-sm transition-colors border ${active ? "bg-[#1B3B28] text-[#FCFBF7] border-[#1B3B28]" : "bg-[#FCFBF7] text-[#1B3B28] border-[#1B3B28]/15 hover:bg-[#E6ECE4]"}`}>
    {ANSWERS[k].label}
  </button>
);

export default function CheckinPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [answer, setAnswer] = useState(ANSWERS[params.get("r")] ? params.get("r") : null);
  const [nome, setNome] = useState("");
  const [nota, setNota] = useState("");
  const [state, setState] = useState(answer ? "sending" : "idle");

  const submit = async (risposta, n = nota) => {
    setState("sending");
    try {
      const { data } = await axios.post(`${API}/checkin/${token}`, { risposta, nota: n });
      setNome(data.nome); setAnswer(risposta); setState("done");
      return true;
    } catch (e) {
      setState(e.response?.status === 404 ? "invalid" : "error");
      return false;
    }
  };

  useEffect(() => { if (answer) submit(answer, ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const a = answer && ANSWERS[answer];
  return (
    <div className="min-h-screen bg-[#F4F3EC] px-6 py-10 sm:py-16" data-testid="checkin-page">
      <div className="mx-auto max-w-2xl">
        <Logo />
        {state === "invalid" && <p className="mt-16 f-serif text-4xl text-[#1B3B28]" data-testid="checkin-invalid">Link non valido o scaduto.</p>}
        {state === "error" && <p className="mt-16 text-[#9A4B3C]" data-testid="checkin-error">Qualcosa non ha funzionato. Riprova tra poco o scrivici su WhatsApp.</p>}
        {state === "sending" && <Loader2 className="mt-16 animate-spin text-[#3E6B48]" />}
        {(state === "idle" || state === "done") && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-14">
            <p className="eyebrow text-[#3E6B48]">{state === "done" ? "Risposta ricevuta" : "Come ti senti?"}</p>
            <AnimatePresence mode="wait">
              <motion.div key={answer || "none"} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h1 className="f-serif text-5xl sm:text-6xl text-[#1B3B28] mt-3 leading-[1]" data-testid="checkin-title">
                  {a ? <>Grazie{nome && `, ${nome}`}.<br /><em className="text-[#3E6B48]">{a.title}</em></> : "Sono passati tre giorni dalla seduta."}
                </h1>
                {a && <p className="mt-6 text-[#526356] leading-relaxed max-w-xl" data-testid="checkin-message">{a.text}</p>}
              </motion.div>
            </AnimatePresence>
            {answer === "aiuto" && (
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="checkin-whatsapp-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3.5 hover:bg-[#3E6B48] transition-colors">
                <MessageCircle size={16} /> Scrivici su WhatsApp
              </a>
            )}
            <div className="mt-10">
              <p className="text-sm text-[#526356]">{answer ? "Vuoi cambiare risposta?" : "Scegli con un tocco:"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.keys(ANSWERS).map((k) => <Choice key={k} k={k} active={k === answer} onPick={(v) => submit(v)} />)}
              </div>
            </div>
            {answer && (
              <div className="mt-10 rounded-[1.75rem] bg-[#FCFBF7] border border-[#1B3B28]/10 p-6">
                <label className="text-sm text-[#526356]" htmlFor="checkin-note">Vuoi aggiungere qualcosa? (facoltativo)</label>
                <textarea id="checkin-note" rows={3} maxLength={1000} value={nota} onChange={(e) => setNota(e.target.value)} data-testid="checkin-note-input"
                  className="mt-2 w-full rounded-2xl border border-[#1B3B28]/15 bg-[#F4F3EC] px-4 py-3 text-[#1B3B28] resize-none focus:outline-none focus:ring-2 focus:ring-[#86C89B]" placeholder="Raccontaci come va" />
                <button onClick={async () => { if (await submit(answer)) toast.success("Nota inviata al Dott. Mauro."); }} disabled={!nota.trim()} data-testid="checkin-note-submit"
                  className="mt-3 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-5 py-2.5 text-sm disabled:opacity-40 hover:bg-[#3E6B48] transition-colors">Invia nota</button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
